/**
 * Query-string login for Amplify pull request previews (pr-<n>.<appId>.amplifyapp.com).
 *
 * The classes app is served from learn / staging.learn / qa.learn.mindbodygreen.com and reads the
 * cl_ac (and, for free-trial previewers, h_id / c_ps) cookies that the login site sets on the
 * shared mindbodygreen.com domain. A preview build is served from amplifyapp.com, never sees those
 * cookies, and so used to send every visitor to the production logout page.
 *
 * This turns `?id=<sessionId>` (and `?hId=...&cPs=...`) into those cookies on the preview host,
 * the same query params the Rainforest QA tests use. It only runs when both are true:
 *  - the build turned it on (PREVIEW_LOGIN_ENABLED, set in next.config.js only for PR previews
 *    or an explicit ENABLE_PREVIEW_LOGIN=true, and never for the production branch), and
 *  - the page is served from an amplifyapp.com host or localhost.
 * A mindbodygreen.com host (learn.*, www, ...) never runs it, so it can't affect the real sites.
 */
import cookies from 'js-cookie';

const PREVIEW_HOST_PATTERN = /(^|\.)amplifyapp\.com$|^localhost$/;

const SESSION_COOKIE = 'cl_ac';
const HASH_ID_COOKIE = 'h_id';
const PREVIEW_TOKEN_COOKIE = 'c_ps';

const LOGIN_PARAMS = ['id', 'hId', 'cPs'];

export const isPreviewLoginBuild = () => process.env.PREVIEW_LOGIN_ENABLED === 'true';

export const isPreviewLoginHost = (hostname) => PREVIEW_HOST_PATTERN.test(hostname || '');

export const isPreviewLoginActive = (hostname) =>
  isPreviewLoginBuild() && isPreviewLoginHost(hostname);

/**
 * Works out which login cookies a URL asks for. When any login param is present, the URL defines
 * the whole identity, the way the QA site's links are written:
 *  - `?id=X`                 logged-in user (session X), previewer cookies cleared
 *  - `?hId=H&cPs=T`          logged-out free-trial previewer, session cookie cleared
 *  - `?id=X&hId=H&cPs=T`     logged-in previewer
 *
 * @returns {null | { set: Object<string, string>, remove: string[], search: string }}
 *   null when the URL has no login params; otherwise the cookies to set and remove, and the
 *   query string with the login params taken out (so the session id doesn't linger in the URL).
 */
export const parsePreviewLogin = (search) => {
  const params = new URLSearchParams(search || '');
  if (!LOGIN_PARAMS.some((p) => params.get(p))) return null;

  const sessionId = params.get('id');
  const hashId = params.get('hId');
  const previewToken = params.get('cPs');

  const set = {};
  const remove = [];

  if (sessionId) set[SESSION_COOKIE] = sessionId;
  else remove.push(SESSION_COOKIE);

  if (hashId && previewToken) {
    set[HASH_ID_COOKIE] = hashId;
    set[PREVIEW_TOKEN_COOKIE] = previewToken;
  } else {
    remove.push(HASH_ID_COOKIE, PREVIEW_TOKEN_COOKIE);
  }

  LOGIN_PARAMS.forEach((p) => params.delete(p));
  const rest = params.toString();

  return { set, remove, search: rest ? `?${rest}` : '' };
};

/**
 * Browser only. Applies the login params in the current URL as host-only cookies, then reloads
 * the page without them.
 *
 * It reloads instead of using history.replaceState because the Next router reads the URL before
 * _app runs and, on a static export, puts that URL (with ?id=) back after hydration.
 *
 * @returns {boolean} true when it set cookies and started the reload
 */
export const applyPreviewLogin = () => {
  if (!isPreviewLoginActive(window.location.hostname)) return false;

  const login = parsePreviewLogin(window.location.search);
  if (!login) return false;

  const options = { path: '/', sameSite: 'Lax', secure: window.location.protocol === 'https:' };
  login.remove.forEach((name) => cookies.remove(name, { path: '/' }));
  Object.entries(login.set).forEach(([name, value]) => cookies.set(name, value, options));

  const { pathname, hash } = window.location;
  window.location.replace(`${pathname}${login.search}${hash}`);
  return true;
};
